/**
 * A tela Pessoas (13.4, chamado de 21/10/2026, P13; FR-40, PN2).
 *
 * "Não tem onde cadastrar as pessoas" (print 16): a Pessoa só aparecia dentro da ficha da Empresa,
 * e a API recusava a listagem geral. Esta tela é o lugar no menu para **achar, cadastrar e
 * vincular** contatos: todos os que quem entrou enxerga, com a Empresa ao lado, paginados, com busca
 * por nome e por telefone.
 *
 * **No molde de `ListaDeEmpresas.tsx`, e com as classes dela** (`lista-de-empresas.css`): tabela no
 * computador e cards no celular (`useEhCelular`), a busca e a página na URL (`rotas/pessoas.tsx`),
 * `keepPreviousData` com "Atualizando…" e a correção de página além do fim. O desenho é o mesmo,
 * de propósito: é "a tela nova no estilo do protótipo" que a proposta pediu.
 *
 * **O que cada um vê é da RLS do `cadastro`, e não da tela**: o Vendedor recebe as Pessoas das
 * Empresas que enxerga (FR-30) e só as Pessoas soltas que **recebeu** pelo WhatsApp; a gestão, todas.
 *
 * **A busca vai ao serviço como foi digitada.** É ele que decide se o termo é nome (sem acento nem
 * pontuação) ou telefone (pela chave normalizada, a mesma da resolução do WhatsApp): "(22) 99755-0997"
 * acha quem tem `22997550997` e quem tem `5522997550997`. A tela não tem a regra.
 *
 * **As Empresas da linha (13.19c2)**: a Pessoa de várias Empresas é uma linha só, com a primeira
 * Empresa visível (o link da ficha, o nome exibido) e "+N" das demais visíveis — o nome delas no
 * rótulo acessível e no `title`. Uma Empresa que quem olha não vê não vem do serviço, e por isso não
 * é nomeada nem contada.
 *
 * **A Pessoa solta** (sem Empresa) mostra "Sem empresa" e, para quem tem `editar_empresa`,
 * "Vincular a uma Empresa" — que abre logo abaixo da linha o **mesmo** componente do painel do
 * WhatsApp (`VinculoDaPessoaSolta`, 13.3), e não um modal (D-10: o "Cadastrar Empresa com este
 * contato" já abre o dele). O desfecho mora **no nível da lista**, porque a linha muda de forma
 * quando a Pessoa ganha Empresa: o aviso em `role="status"`, a recusa terminal em `role="alert"`, e
 * o foco vai para o link da Empresa da linha.
 *
 * **"+ Pessoa"** (com `editar_empresa`) cadastra numa Empresa escolhida — o cadastro de sempre, que
 * desde a 13.4 **adota** a solta com o mesmo telefone: a tela diz qual dos dois aconteceu. Desde a
 * 13.15 (chamado de 26/10, B), com `criar_empresa`, a Empresa que não existe se cria ali mesmo: o
 * "Criar nova empresa" abre a ficha completa (`ModalDaNovaEmpresa`, sem o cartão de Pessoas), e a
 * gravada volta escolhida, com o resto do "+ Pessoa" preenchido.
 *
 * **A ordem (13.15, A):** as ações, os painéis ("+ Pessoa", o gerenciador) e os avisos ficam
 * **acima** da barra de busca, que fica colada à tabela.
 *
 * **As etiquetas da equipe (13.6, FR-86)**: cada linha mostra as da Pessoa e, com `editar_empresa`,
 * aplica e retira ali mesmo (D-7) — é por aqui que se "limpa primeiro" uma etiqueta de Pessoa antes
 * de apagá-la. Desde a 13.15 (H), no modo compacto: dois chips numa linha, "+N", e o "+ Etiqueta",
 * que abre aplicar, retirar e criar num painel em portal. A barra tem o filtro por etiqueta e o
 * botão "Etiquetas" (o gerenciador, de todos), e quem chega pelo "Ver pessoas" do aviso de apagar
 * lê quantas Pessoas com ela não enxerga (D-11).
 *
 * Esconder botão por permissão é conveniência, e não autorização: quem recusa é o serviço.
 */

import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  type FormEvent,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useSessao } from '@/modulos/identidade/sessao'
import { CAMINHO_DA_EMPRESA } from '@/rotas/empresa'
import { AcoesDoCabecalho, useDefinirSubtitulo, useEhCelular } from '@/shell/Shell'
import { CampoDeEmpresa } from './CampoDeEmpresa'
import { LENDO_A_EMPRESA, useEscolhaDeEmpresa } from './escolha-da-empresa'
import { nomeExibido } from './nome-da-empresa'
import { telefoneNaTela } from './telefone-na-tela'
import {
  BotaoDeEtiquetas,
  EtiquetasDoAlvo,
  FiltroPorEtiqueta,
  GerenciadorDeEtiquetas,
  FILTRO_SEM_A_ETIQUETA,
  useAvisoDasEscondidas,
  useEtiquetaDoFiltro,
} from './etiquetas'
import { ModalDaNovaEmpresa, telefoneDaConversaNaFicha } from './ModalDaNovaEmpresa'
import { PerguntaDaAdocao, type RespostaDaAdocao, corpoDaResposta } from './PerguntaDaAdocao'
import {
  CADASTRADA,
  type CampoDaRecusa,
  QuatroCampos,
  RASCUNHO_VAZIO,
  type Rascunho,
  campoDaFalha,
  campoDaRecusaLocal,
  corpoDe,
  recusaDaPessoa,
} from './ResponsaveisDaEmpresa'
import {
  VinculoDaPessoaSolta,
  contatoVinculadoA,
} from './VinculoDaPessoaSolta'
import {
  CHAVE_DOS_LOTES_DE_PESSOAS,
  CONTATO_DO_WHATSAPP_VINCULADO,
  type ContatoPerguntado,
  type PessoaNaLista,
  TAMANHO_DA_BUSCA,
  cadastrarPessoa,
  chaveDasPessoas,
  contatoDaPergunta,
  etiquetaIdsDe,
  foiAdotada,
  lerPessoasPorIds,
  usePessoasDaBase,
} from './api'
import './lista-de-empresas.css'
import './lista-de-pessoas.css'

/** Quantas Pessoas a tela pede por página — o padrão da de Empresas. */
export const LIMITE_DA_PAGINA = 50

/** O nome da região da tela — distinto do título do shell ("Pessoas"), como "Base comercial". */
export const NOME_DA_TELA_DE_PESSOAS = 'Base de contatos'
export const ROTULO_DA_BUSCA = 'Buscar pessoa'
export const NOME_DA_LISTA_DE_PESSOAS = 'Pessoas, em ordem alfabética'
export const SEM_EMPRESA = 'Sem empresa'
export const VINCULAR_A_UMA_EMPRESA = 'Vincular a uma Empresa'
export const FECHAR_O_VINCULO = 'Fechar'
/** O nome acessível do "+ Pessoa" do cabeçalho e do formulário que ele abre. */
export const NOVA_PESSOA = 'Nova pessoa'
/** A confirmação do cadastro sem adoção — a mesma da ficha (`ResponsaveisDaEmpresa`). */
export const PESSOA_CADASTRADA = CADASTRADA
export const ESCOLHA_A_EMPRESA = 'Escolha a Empresa da pessoa.'
export const SEM_PESSOAS = 'Nenhuma pessoa cadastrada ainda.'
export const SEM_RESULTADO = 'Nenhuma pessoa encontrada.'
const PAGINA_VAZIA = 'Esta página não existe mais. Voltando para a última.'
const CARREGANDO = 'Carregando as pessoas…'
const ATUALIZANDO = 'Atualizando…'
const TENTAR_DE_NOVO = 'Tentar de novo'
const VAZIO = '—'

const ID_DO_TITULO = 'pessoas-titulo'
const ID_DA_BUSCA = 'pessoas-busca'
const ID_DO_CAMPO_DA_EMPRESA_DA_NOVA = 'pessoas-nova-empresa'
const ID_DO_GERENCIADOR = 'pessoas-etiquetas'
const PERMISSAO_DE_EDITAR = 'editar_empresa'
/** "Criar nova empresa" no "+ Pessoa" (13.15, B): só com ela — conveniência, e não autorização. */
const PERMISSAO_DE_CRIAR_EMPRESA = 'criar_empresa'

const NUMERO = new Intl.NumberFormat('pt-BR')

/** O aviso da tela: o desfecho do vínculo ou do "+ Pessoa", fora da linha que muda de forma. */
type Aviso = { tipo: 'status' | 'alert'; texto: string } | null

function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O telefone como a tela o mostra — desde a 13.19c1 em `telefone-na-tela.ts`, porque a sugestão de
 * Pessoas da ficha nova o usa também (e a ficha nova não pode importar esta tela, que a importa).
 */
export { telefoneNaTela }

function temLetra(texto: string): boolean {
  return /\p{L}/u.test(texto)
}

/** A coluna Nome da Pessoa sem nome com letra e sem número nenhum a mostrar (13.14a, DW-391). */
export const SEM_NOME = 'Sem nome'

/** O telefone da linha: o gravado, ou o nome-número da solta que não tem telefone (só com dígito:
 *  a pontuação sozinha não é número). O telefone gravado em branco vale como ausente (code review
 *  de 29/10/2026): senão a coluna Nome mostraria o traço do telefone vazio. */
function telefoneDaLinha(pessoa: PessoaNaLista): string | null {
  if (pessoa.telefone !== null && pessoa.telefone.trim() !== '') {
    return pessoa.telefone
  }
  return !temLetra(pessoa.nome) && /\d/.test(pessoa.nome) ? pessoa.nome : null
}

/**
 * O nome na lista. **Desde 29/10/2026 (DW-361, o Gustavo: "tem que ser uma coisa só")** o nome é o
 * da Pessoa, e o do WhatsApp chega a ela pelo `cadastro`; a Pessoa que ainda não tem nome com letra
 * — a solta, que nasce com o número por nome — mostra o **telefone formatado** na coluna Nome, e
 * nunca a célula em branco da 13.14a. Sem número nenhum (o nome "---" sem telefone), "Sem nome".
 */
function NomeNaLista({ pessoa }: { pessoa: PessoaNaLista }) {
  if (temLetra(pessoa.nome)) {
    return <>{pessoa.nome}</>
  }
  const telefone = telefoneDaLinha(pessoa)
  if (telefone === null) {
    return <>{SEM_NOME}</>
  }
  return <span className="numero">{telefoneNaTela(telefone)}</span>
}

export interface PropsDaListaDePessoas {
  busca: string
  pagina: number
  /** A etiqueta da equipe do filtro (13.6), como a URL a carrega. */
  etiqueta?: string
  aoTrocarABusca: (termo: string) => void
  aoTrocarAEtiqueta?: (etiquetaId: string | undefined) => void
  aoTrocarAPagina: (pagina: number, opcoes?: { substituir?: boolean }) => void
}

/**
 * A linha que quem olha edita (13.20c, code review A4): a permissão **e** o `pode_editar` da Pessoa
 * — falso para a Pessoa lida só pelo Negócio sem Empresa que quem olha recebeu, que aparece sem
 * "Vincular" e sem "+ Etiqueta". Só o `false` explícito trava (o `cadastro` anterior ao campo não o
 * manda). Conveniência: o servidor recusa as escritas com o `404` de Pessoa.
 */
function podeEditarAPessoa(podeEditar: boolean, pessoa: PessoaNaLista): boolean {
  return podeEditar && (pessoa as Partial<PessoaNaLista>).pode_editar !== false
}

/** O nome acessível do "+N" das demais Empresas da linha (13.19c2): quantas, e quais. */
export function maisEmpresas(nomes: readonly string[]): string {
  return `Mais ${String(nomes.length)}: ${nomes.join(', ')}`
}

/**
 * A Empresa da linha: a primeira visível (o link para a ficha) e "+N" das demais (13.19c2), ou "Sem
 * empresa" com o botão do vínculo.
 */
function EmpresaDaLinha({
  pessoa,
  podeEditar,
  aberto,
  aoAbrir,
}: {
  pessoa: PessoaNaLista
  podeEditar: boolean
  aberto: boolean
  aoAbrir: () => void
}) {
  /* Sem o campo (o `cadastro` anterior à 13.19c2, no rollback) é "não sei": o traço, e não
     "Sem empresa" nem o vínculo (code review, E1). */
  const empresas = (pessoa as { empresas?: PessoaNaLista['empresas'] }).empresas
  if (empresas === undefined) {
    return <span className="empresas__celula--apagada">{VAZIO}</span>
  }
  const [primeira, ...demais] = empresas
  if (primeira !== undefined) {
    /* O nome exibido da Empresa (13.7): o nome fantasia, e a razão social sem ele. */
    const nomesDasDemais = demais.map((empresa) =>
      nomeExibido(empresa.razao_social, empresa.nome_fantasia),
    )
    return (
      <span className="pessoas__empresas">
        <Link
          to={CAMINHO_DA_EMPRESA}
          params={{ empresaId: primeira.id }}
          className="empresas__link pessoas__primeira-empresa"
          data-empresa-da-pessoa={pessoa.id}
        >
          {nomeExibido(primeira.razao_social, primeira.nome_fantasia)}
        </Link>
        {demais.length === 0 ? null : (
          <span
            className="etiqueta etiqueta--mais pessoas__mais-empresas"
            role="img"
            aria-label={maisEmpresas(nomesDasDemais)}
            title={nomesDasDemais.join(', ')}
          >
            {`+${String(demais.length)}`}
          </span>
        )}
      </span>
    )
  }
  return (
    <span className="pessoas__sem-empresa">
      <span className="empresas__celula--apagada">{SEM_EMPRESA}</span>
      {podeEditar && !aberto ? (
        <button type="button" className="empresas__botao empresas__botao--discreto" onClick={aoAbrir}>
          {VINCULAR_A_UMA_EMPRESA}
        </button>
      ) : null}
    </span>
  )
}

export function ListaDePessoas({
  busca,
  pagina,
  etiqueta,
  aoTrocarABusca,
  aoTrocarAEtiqueta,
  aoTrocarAPagina,
}: PropsDaListaDePessoas) {
  const [gerenciando, setGerenciando] = useState(false)
  const botaoDeEtiquetas = useRef<HTMLButtonElement>(null)
  const ehCelular = useEhCelular()
  const { data: sessao } = useSessao()
  const podeEditar = sessao !== undefined && PERMISSAO_DE_EDITAR in sessao.permissoes
  const podeCriarEmpresa = sessao !== undefined && PERMISSAO_DE_CRIAR_EMPRESA in sessao.permissoes
  const podeEditarALinha = (pessoa: PessoaNaLista) => podeEditarAPessoa(podeEditar, pessoa)
  const clienteDeConsultas = useQueryClient()
  const secao = useRef<HTMLElement>(null)
  const titulo = useRef<HTMLHeadingElement>(null)

  const termoConsultado = useDeferredValue(busca)
  /* 13.18: a etiqueta da URL que o catálogo de pessoa não tem não vai ao serviço — a lista nem sai. */
  const filtro = useEtiquetaDoFiltro('pessoa', etiqueta)
  const consulta = usePessoasDaBase(
    {
      busca: termoConsultado,
      pagina,
      limite: LIMITE_DA_PAGINA,
      etiquetaId: etiqueta,
    },
    { habilitada: filtro.pronta },
  )
  const itensDaConsulta = useMemo(() => consulta.data?.itens ?? [], [consulta.data])
  const total = consulta.data?.total ?? 0

  const [aviso, setAviso] = useState<Aviso>(null)
  /** A Pessoa solta cujo vínculo está aberto abaixo da linha. */
  const [aberto, setAbertoId] = useState<string | null>(null)
  const setAberto = (pessoa: PessoaNaLista | null) => {
    setAbertoId(pessoa?.id ?? null)
  }
  /* O vínculo em curso, ou o modal dele aberto: o "Fechar" fica travado (o painel faz o mesmo). */
  const [vinculoEmCurso, setVinculoEmCurso] = useState({ ocupado: false, modalAberto: false })
  /* As linhas de quando o vínculo travou (ver abaixo), ou `null` com ele livre. */
  const [linhasCongeladas, setLinhasCongeladas] = useState<readonly PessoaNaLista[] | null>(null)
  const linhasDeAgora = useRef(itensDaConsulta)
  useEffect(() => {
    linhasDeAgora.current = itensDaConsulta
  }, [itensDaConsulta])
  const aoMudarOcupacao = useCallback((estado: { ocupado: boolean; modalAberto: boolean }) => {
    const travado = estado.ocupado || estado.modalAberto
    setLinhasCongeladas((atuais) => (travado ? (atuais ?? linhasDeAgora.current) : null))
    setVinculoEmCurso((atual) =>
      atual.ocupado === estado.ocupado && atual.modalAberto === estado.modalAberto ? atual : estado,
    )
  }, [])
  const vinculoTravado = vinculoEmCurso.ocupado || vinculoEmCurso.modalAberto
  /*
   * **Com o vínculo em curso ou o modal dele aberto, a lista não muda debaixo dele.** A linha de
   * Pessoa do modal pode adotar esta mesma solta e mudar o nome dela, e a releitura a tiraria do
   * lugar (a ordem é por nome) ou da página (a busca deixa de alcançá-la): o vínculo, abaixo da
   * linha, desmontaria com o modal no meio da cadeia. As linhas mostradas ficam as de antes até o
   * vínculo terminar, e então a lista relida assenta.
   */
  const itens = linhasCongeladas ?? itensDaConsulta
  /** A Pessoa cujo link da Empresa recebe o foco depois do vínculo (quando a lista relida chegar). */
  const [focoNaEmpresaDe, setFocoNaEmpresaDe] = useState<string | null>(null)

  const avisoDasEscondidas = useAvisoDasEscondidas({
    tipo: 'pessoas',
    etiquetaId: etiqueta,
    busca: termoConsultado,
    totalVisivel:
      consulta.data === undefined || consulta.isPlaceholderData || consulta.isFetching
        ? undefined
        : total,
  })

  useDefinirSubtitulo(
    consulta.data === undefined
      ? undefined
      : `${NUMERO.format(total)} pessoa${total === 1 ? '' : 's'}`,
  )

  function relerALista() {
    return clienteDeConsultas.invalidateQueries({ queryKey: CHAVE_DOS_LOTES_DE_PESSOAS })
  }

  /* O foco depois do vínculo: o link da Empresa da linha, quando a lista relida o trouxer; se a
     linha saiu da página (a busca não a alcança mais), o título da lista. */
  useEffect(() => {
    if (focoNaEmpresaDe === null || consulta.isFetching) {
      return
    }
    const link = secao.current?.querySelector<HTMLElement>(
      `[data-empresa-da-pessoa="${focoNaEmpresaDe}"]`,
    )
    if (link !== null && link !== undefined) {
      link.focus()
    } else if (!itens.some((item) => item.id === focoNaEmpresaDe)) {
      titulo.current?.focus()
    } else {
      return
    }
    setFocoNaEmpresaDe(null)
  }, [focoNaEmpresaDe, consulta.isFetching, itens])

  /*
   * O painel de etiquetas de uma linha desmontou aberto (13.15, H). Só se a linha **saiu da
   * lista** (a etiqueta do filtro foi retirada) o foco vai ao título; a troca de largura (a linha
   * volta como card ou como linha de tabela) e a saída da tela não o levam a lugar nenhum. A
   * conferência espera a vez seguinte da fila: a desmontagem acontece no meio do commit, antes de a
   * lista assentar.
   */
  const itensMostrados = useRef(itens)
  useEffect(() => {
    itensMostrados.current = itens
  }, [itens])
  const aoSumirComOPainel = (pessoaId: string) => {
    setTimeout(() => {
      if (!itensMostrados.current.some((item) => item.id === pessoaId)) {
        titulo.current?.focus()
      }
    }, 0)
  }

  /* --- O "+ Pessoa" --- */
  const [novaAberta, setNovaAberta] = useState(false)
  /* A Empresa do "+ Pessoa": escolhida da lista, ou criada no modal (13.15, B). */
  const empresaDaNova = useEscolhaDeEmpresa(ID_DO_CAMPO_DA_EMPRESA_DA_NOVA)
  const [rascunho, setRascunho] = useState<Rascunho>(RASCUNHO_VAZIO)
  const [recusaDaNova, setRecusaDaNova] = useState<string | null>(null)
  /** O campo de que a recusa fala (13.17): o da conferência local, ou o do código do serviço. */
  const [campoDaRecusaDaNova, setCampoDaRecusaDaNova] = useState<CampoDaRecusa>(null)
  /* A pergunta da adoção (13.14a, DW-388): o contato com outro nome, respondido pelos botões. */
  const [perguntaDaNova, setPerguntaDaNova] = useState<ContatoPerguntado | null>(null)
  const [salvando, setSalvando] = useState(false)

  /* Aberto o "+ Pessoa", o foco vai ao primeiro campo do formulário (a Empresa). */
  useEffect(() => {
    if (novaAberta) {
      document.getElementById(ID_DO_CAMPO_DA_EMPRESA_DA_NOVA)?.focus()
    }
  }, [novaAberta])

  function fecharANova() {
    setNovaAberta(false)
    empresaDaNova.limpar()
    setRascunho(RASCUNHO_VAZIO)
    setRecusaDaNova(null)
    setPerguntaDaNova(null)
  }

  async function salvarANova(
    evento: FormEvent<HTMLFormElement> | null,
    resposta: RespostaDaAdocao | null = null,
  ) {
    evento?.preventDefault()
    /* A ficha achada pelo ERP ainda está sendo lida: o "Salvar" espera o nome dela. */
    if (salvando || empresaDaNova.lendo) {
      return
    }
    const empresa = empresaDaNova.escolhida
    if (empresa === null) {
      setRecusaDaNova(ESCOLHA_A_EMPRESA)
      setCampoDaRecusaDaNova(null)
      return
    }
    const local = recusaDaPessoa(rascunho)
    if (local !== null) {
      setRecusaDaNova(local)
      setCampoDaRecusaDaNova(campoDaRecusaLocal(local))
      return
    }
    setSalvando(true)
    setRecusaDaNova(null)
    setPerguntaDaNova(null)
    try {
      const cadastrada = await cadastrarPessoa(empresa.id, {
        ...corpoDe(rascunho),
        principal: false,
        ...corpoDaResposta(resposta),
      })
      setAviso({
        tipo: 'status',
        texto: foiAdotada(cadastrada) ? CONTATO_DO_WHATSAPP_VINCULADO : PESSOA_CADASTRADA,
      })
      fecharANova()
      await Promise.all([
        relerALista(),
        clienteDeConsultas.invalidateQueries({ queryKey: chaveDasPessoas(empresa.id) }),
      ])
    } catch (falha) {
      setRecusaDaNova(mensagemDe(falha))
      setCampoDaRecusaDaNova(campoDaFalha(falha))
      setPerguntaDaNova(contatoDaPergunta(falha) ?? null)
    } finally {
      setSalvando(false)
    }
  }

  /* --- A paginação, no molde de Empresas --- */
  const totalDePaginas = Math.max(1, Math.ceil(total / LIMITE_DA_PAGINA))
  const carregando = !consulta.isError && (consulta.isLoading || consulta.data === undefined)
  const atualizando = consulta.isPlaceholderData
  const primeiroDaPagina = (pagina - 1) * LIMITE_DA_PAGINA + 1
  const posicao =
    itens.length === 0
      ? `Página ${String(pagina)} de ${String(totalDePaginas)}`
      : `${NUMERO.format(primeiroDaPagina)}–${NUMERO.format(
          primeiroDaPagina + itens.length - 1,
        )} de ${NUMERO.format(total)} · página ${String(pagina)} de ${String(totalDePaginas)}`
  const alemDoFim =
    !carregando && !atualizando && consulta.data !== undefined && pagina > totalDePaginas
  useEffect(() => {
    if (alemDoFim) {
      aoTrocarAPagina(totalDePaginas, { substituir: true })
    }
  }, [alemDoFim, aoTrocarAPagina, totalDePaginas])

  function mensagemDeVazio(): string {
    if (alemDoFim) {
      return PAGINA_VAZIA
    }
    return termoConsultado.trim() === '' ? SEM_PESSOAS : SEM_RESULTADO
  }

  function vinculoDa(pessoa: PessoaNaLista) {
    return (
      <div className="pessoas__vinculo">
        <VinculoDaPessoaSolta
          idDoCampo={`pessoas-vinculo-${pessoa.id}`}
          pessoaId={pessoa.id}
          relerAPessoa={async () => (await lerPessoasPorIds([pessoa.id]))[0]}
          nomeDaFicha={temLetra(pessoa.nome) ? pessoa.nome : ''}
          telefoneDaFicha={pessoa.telefone === null ? '' : telefoneDaConversaNaFicha(pessoa.telefone)}
          aoMudarOcupacao={aoMudarOcupacao}
          aoVincular={async (empresa) => {
            setAviso({ tipo: 'status', texto: contatoVinculadoA(empresa.nome) })
            setAberto(null)
            setFocoNaEmpresaDe(pessoa.id)
            await relerALista()
          }}
          aoRecusar={(mensagem) => {
            setAviso({ tipo: 'alert', texto: mensagem })
            setAberto(null)
            void relerALista()
          }}
        />
        <button
          type="button"
          className="empresas__botao empresas__botao--discreto"
          disabled={vinculoTravado}
          onClick={() => {
            setAberto(null)
          }}
        >
          {FECHAR_O_VINCULO}
        </button>
      </div>
    )
  }

  return (
    <section
      className="empresas pessoas"
      aria-labelledby={ID_DO_TITULO}
      ref={secao}
      data-largura={ehCelular ? 'celular' : 'computador'}
    >
      <div className="empresas__cartao">
        <div className="empresas__cabecalho">
          <h2
            className="empresas__titulo empresas__so-para-leitor"
            id={ID_DO_TITULO}
            ref={titulo}
            tabIndex={-1}
          >
            {NOME_DA_TELA_DE_PESSOAS}
          </h2>
        </div>

        {/*
         * A ordem da tela (13.15, chamado de 26/10, A), nas duas larguras: as ações ("+ Pessoa"),
         * os painéis e os avisos, e só então a barra (busca, filtro, contagem) colada à tabela. Os
         * painéis abriam entre a barra e a tabela e afastavam a busca das colunas que ela filtra.
         */}
        {podeEditar ? (
          <AcoesDoCabecalho>
            <button
              type="button"
              className="empresas__botao empresas__botao--primario"
              aria-label={NOVA_PESSOA}
              aria-expanded={novaAberta}
              onClick={() => {
                setAviso(null)
                setNovaAberta(true)
              }}
            >
              + Pessoa
            </button>
          </AcoesDoCabecalho>
        ) : null}

        {gerenciando ? (
          <GerenciadorDeEtiquetas
            tipo="pessoa"
            id={ID_DO_GERENCIADOR}
            aoFechar={() => {
              setGerenciando(false)
              botaoDeEtiquetas.current?.focus()
            }}
            etiquetaDoFiltro={etiqueta}
            aoApagarAEtiquetaDoFiltro={() => {
              aoTrocarAEtiqueta?.(undefined)
            }}
          />
        ) : null}

        {novaAberta ? (
          <form
            className="pessoas__nova"
            aria-label={NOVA_PESSOA}
            onSubmit={(evento) => void salvarANova(evento)}
            noValidate
          >
            <CampoDeEmpresa
              key={empresaDaNova.chaveDoCampo}
              id={ID_DO_CAMPO_DA_EMPRESA_DA_NOVA}
              escolhida={empresaDaNova.noCampo}
              obrigatorio
              placeholder={empresaDaNova.lendo ? LENDO_A_EMPRESA : undefined}
              erro={empresaDaNova.erroDaLeitura}
              aoEscolher={(empresa) => {
                setRecusaDaNova(null)
                setPerguntaDaNova(null)
                empresaDaNova.escolher(empresa)
              }}
              {...(podeCriarEmpresa
                ? {
                    aoCriarNova: (texto: string) => {
                      setRecusaDaNova(null)
                      setPerguntaDaNova(null)
                      empresaDaNova.abrirAFicha(texto)
                    },
                  }
                : {})}
            />
            <div className="pessoas__campos">
              <QuatroCampos
                sufixo="pessoas-nova"
                rascunho={rascunho}
                desabilitado={salvando}
                recusaDoNome={
                  recusaDaNova !== null && campoDaRecusaDaNova === 'nome'
                    ? 'pessoas-nova-recusa'
                    : null
                }
                recusaDoTelefone={
                  recusaDaNova !== null && campoDaRecusaDaNova === 'telefone'
                    ? 'pessoas-nova-recusa'
                    : null
                }
                aoTrocar={(campo, valor) => {
                  setRecusaDaNova(null)
                  setPerguntaDaNova(null)
                  setRascunho((atual) => ({ ...atual, [campo]: valor }))
                }}
              />
            </div>
            {recusaDaNova === null ? null : (
              <p className="empresas__erro" role="alert" id="pessoas-nova-recusa">
                {recusaDaNova}
              </p>
            )}
            {perguntaDaNova === null ? null : (
              <PerguntaDaAdocao
                contato={perguntaDaNova}
                desabilitada={salvando}
                aoResponder={(resposta) => void salvarANova(null, resposta)}
              />
            )}
            <div className="pessoas__botoes">
              <button
                type="submit"
                className="empresas__botao empresas__botao--primario"
                disabled={salvando || empresaDaNova.lendo}
              >
                {salvando ? 'Salvando…' : 'Salvar'}
              </button>
              <button
                type="button"
                className="empresas__botao empresas__botao--discreto"
                disabled={salvando}
                onClick={fecharANova}
              >
                Cancelar
              </button>
            </div>
          </form>
        ) : null}
        {/* Irmão do `<form>`, e não filho (13.15, B): o `submit` da ficha borbulha pela árvore do
            React e dispararia o "Salvar" do "+ Pessoa". Sem o cartão de Pessoas: a Pessoa é
            esta. */}
        {novaAberta && empresaDaNova.fichaAberta !== null ? (
          <ModalDaNovaEmpresa
            textoInicial={empresaDaNova.fichaAberta}
            semCartaoDePessoas
            aoCancelar={empresaDaNova.fecharAFicha}
            aoEscolher={(empresa) => {
              setRecusaDaNova(null)
              setPerguntaDaNova(null)
              empresaDaNova.aoCriar(empresa)
            }}
            aoEncontrar={(empresaId) => {
              setRecusaDaNova(null)
              setPerguntaDaNova(null)
              empresaDaNova.aoEncontrar(empresaId)
            }}
          />
        ) : null}

        {avisoDasEscondidas === undefined ? null : (
          <p className="pessoas__aviso" role="status">
            {avisoDasEscondidas}
          </p>
        )}
        {aviso === null ? null : (
          <p
            className={aviso.tipo === 'alert' ? 'empresas__erro pessoas__aviso' : 'pessoas__aviso'}
            role={aviso.tipo}
          >
            {aviso.texto}
          </p>
        )}

        <div className="empresas__ferramentas">
          <div className="empresas__campo">
            <label className="empresas__rotulo empresas__so-para-leitor" htmlFor={ID_DA_BUSCA}>
              {ROTULO_DA_BUSCA}
            </label>
            <input
              id={ID_DA_BUSCA}
              className="empresas__entrada"
              type="search"
              value={busca}
              maxLength={TAMANHO_DA_BUSCA}
              placeholder="Buscar por nome ou telefone"
              onChange={(evento) => {
                aoTrocarABusca(evento.target.value)
              }}
            />
          </div>
          <div className="empresas__direita">
            <FiltroPorEtiqueta
              tipo="pessoa"
              valor={etiqueta}
              aoTrocar={(etiquetaId) => {
                aoTrocarAEtiqueta?.(etiquetaId)
              }}
            />
            <BotaoDeEtiquetas
              aberto={gerenciando}
              controla={ID_DO_GERENCIADOR}
              refDoBotao={botaoDeEtiquetas}
              aoTrocar={() => {
                setGerenciando((aberto) => !aberto)
              }}
            />
            {consulta.data === undefined ? null : (
              <span className="empresas__contagem">
                {`${NUMERO.format(total)} pessoa${total === 1 ? '' : 's'}`}
              </span>
            )}
          </div>
        </div>

        <div
          className="empresas__corpo"
          aria-busy={atualizando}
          data-atualizando={atualizando ? 'sim' : 'nao'}
        >
          {filtro.removida ? (
            /* 13.18: a etiqueta da URL não está no catálogo desta tela — "Etiqueta removida" no
               filtro, com "Limpar filtro", e a lista nem foi chamada. */
            <p className="empresas__ajuda" role="status">
              {FILTRO_SEM_A_ETIQUETA}
            </p>
          ) : consulta.isError ? (
            <p className="empresas__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : carregando ? (
            <p className="empresas__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : itens.length === 0 ? (
            <p className="empresas__ajuda">{mensagemDeVazio()}</p>
          ) : ehCelular ? (
            <ul className="empresas__cards" aria-label={NOME_DA_LISTA_DE_PESSOAS}>
              {itens.map((pessoa) => (
                <li key={pessoa.id} className="empresas__card">
                  <div className="empresas__card-topo">
                    <span className="empresas__card-titulo">
                      <NomeNaLista pessoa={pessoa} />
                    </span>
                  </div>
                  {/* Dois `<span>`, e não um texto juntado: o cargo e o telefone são duas
                      informações, e a paridade com a tabela é por informação (NFR-1). */}
                  <span className="empresas__card-resumo">
                    <span>{pessoa.cargo ?? VAZIO}</span>
                    {' · '}
                    <span className="numero">{telefoneNaTela(telefoneDaLinha(pessoa))}</span>
                  </span>
                  <EmpresaDaLinha
                    pessoa={pessoa}
                    podeEditar={podeEditarALinha(pessoa)}
                    aberto={aberto === pessoa.id}
                    aoAbrir={() => {
                      setAviso(null)
                      setAberto(pessoa)
                    }}
                  />
                  {aberto === pessoa.id ? vinculoDa(pessoa) : null}
                  <EtiquetasDoAlvo
                    alvo={{ tipo: 'pessoa', id: pessoa.id }}
                    nomeDoAlvo={pessoa.nome}
                    etiquetaIds={etiquetaIdsDe(pessoa)}
                    podeEditar={podeEditarALinha(pessoa)}
                    compacto
                    aoSumirAberto={() => {
                      aoSumirComOPainel(pessoa.id)
                    }}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="empresas__rolagem" role="region" aria-label="Tabela de pessoas" tabIndex={0}>
              <table className="empresas__tabela pessoas__tabela">
                {/* As larguras no `<colgroup>`, como em Empresas (13.15, H): a coluna Etiquetas
                    tem a dela, e o "+ Etiqueta" fica no mesmo lugar em toda linha. */}
                <colgroup>
                  <col className="pessoas__col--nome" />
                  <col className="pessoas__col--cargo" />
                  <col className="pessoas__col--telefone" />
                  <col className="pessoas__col--empresa" />
                  <col className="pessoas__col--etiquetas" />
                </colgroup>
                <caption className="empresas__legenda">{NOME_DA_LISTA_DE_PESSOAS}</caption>
                <thead>
                  <tr>
                    <th scope="col" className="empresas__coluna">
                      Nome
                    </th>
                    <th scope="col" className="empresas__coluna">
                      Cargo
                    </th>
                    <th scope="col" className="empresas__coluna">
                      Telefone
                    </th>
                    <th scope="col" className="empresas__coluna">
                      Empresa
                    </th>
                    <th scope="col" className="empresas__coluna pessoas__coluna--etiquetas">
                      Etiquetas
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {itens.flatMap((pessoa) => {
                    const linhas = [
                      <tr key={pessoa.id} className="empresas__linha">
                        <th scope="row" className="empresas__celula">
                          <div className="empresas__nome">
                            <NomeNaLista pessoa={pessoa} />
                          </div>
                        </th>
                        <td className="empresas__celula">{pessoa.cargo ?? VAZIO}</td>
                        <td className="empresas__celula numero">
                          {telefoneNaTela(telefoneDaLinha(pessoa))}
                        </td>
                        <td className="empresas__celula">
                          <EmpresaDaLinha
                            pessoa={pessoa}
                            podeEditar={podeEditarALinha(pessoa)}
                            aberto={aberto === pessoa.id}
                            aoAbrir={() => {
                              setAviso(null)
                              setAberto(pessoa)
                            }}
                          />
                        </td>
                        <td className="empresas__celula">
                          <EtiquetasDoAlvo
                            alvo={{ tipo: 'pessoa', id: pessoa.id }}
                            nomeDoAlvo={pessoa.nome}
                            etiquetaIds={etiquetaIdsDe(pessoa)}
                            podeEditar={podeEditarALinha(pessoa)}
                            compacto
                            aoSumirAberto={() => {
                              aoSumirComOPainel(pessoa.id)
                            }}
                          />
                        </td>
                      </tr>,
                    ]
                    if (aberto === pessoa.id) {
                      linhas.push(
                        <tr key={`${pessoa.id}-vinculo`} className="pessoas__linha-do-vinculo">
                          <td colSpan={5} className="empresas__celula">
                            {vinculoDa(pessoa)}
                          </td>
                        </tr>,
                      )
                    }
                    return linhas
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {carregando || filtro.removida ? null : (
          <nav className="empresas__paginacao" aria-label="Paginação">
            <button
              type="button"
              className="empresas__botao empresas__botao--discreto"
              disabled={pagina === 1}
              onClick={() => {
                aoTrocarAPagina(Math.max(1, pagina - 1))
              }}
            >
              Anterior
            </button>
            {consulta.isError ? (
              <button
                type="button"
                className="empresas__botao empresas__botao--discreto"
                onClick={() => {
                  void consulta.refetch()
                }}
              >
                {TENTAR_DE_NOVO}
              </button>
            ) : (
              <span className="empresas__posicao" role="status">
                {atualizando ? ATUALIZANDO : posicao}
              </span>
            )}
            <button
              type="button"
              className="empresas__botao empresas__botao--discreto"
              disabled={!consulta.isError && pagina >= totalDePaginas}
              onClick={() => {
                aoTrocarAPagina(pagina + 1)
              }}
            >
              Próxima
            </button>
          </nav>
        )}
      </div>
    </section>
  )
}
